import { useState } from 'react';
import { Package } from 'lucide-react';

import { cn } from '@/lib/utils';

/** `fill` sets no size: the caller's classes decide (a square that fills a grid cell). */
const SIZE = { xs: 'size-6', sm: 'size-8', md: 'size-10', lg: 'size-14', fill: '' } as const;

/**
 * A product's picture at list size, with a quiet glyph when there is none or
 * it fails to load — never a broken-image icon or a placeholder service.
 */
export function Thumb({
  src,
  alt = '',
  size = 'sm',
  className,
}: {
  src?: string | null;
  alt?: string;
  size?: keyof typeof SIZE;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const show = !!src && !failed && !src.endsWith('/images/default-thumb.jpg');
  return (
    <span className={cn('relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted', SIZE[size], className)}>
      {show ? (
        <img src={src!} alt={alt} loading="lazy" className="size-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <Package className="size-1/2 text-muted-foreground/60" aria-hidden />
      )}
    </span>
  );
}

/** Up to `max` thumbnails overlapping, then "+N". For a lead's products or a session's. */
export function ThumbStack({
  items,
  max = 4,
  size = 'sm',
  className,
}: {
  items: Array<{ key: string; src?: string | null; alt?: string }>;
  max?: number;
  size?: keyof typeof SIZE;
  className?: string;
}) {
  if (!items.length) return <span className="text-xs text-muted-foreground">—</span>;
  const shown = items.slice(0, max);
  const rest = items.length - shown.length;
  return (
    <span className={cn('inline-flex items-center', className)}>
      {shown.map((item, i) => (
        <Thumb key={item.key} src={item.src} alt={item.alt} size={size} className={cn('ring-2 ring-background', i > 0 && '-ms-2')} />
      ))}
      {rest > 0 && (
        <span className={cn('-ms-2 inline-flex items-center justify-center rounded-md border bg-muted text-[11px] font-medium tabular-nums text-muted-foreground ring-2 ring-background', SIZE[size])}>
          +{rest}
        </span>
      )}
    </span>
  );
}
